import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "./Api";
import Header from "./Header";
import Footer from "./Footer";
import { FaPenAlt, FaRegCalendarAlt, FaEye, FaThumbsUp } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const truncate = (html, len = 120) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    const text = div.innerText || "";
    return text.length > len ? text.substring(0, len) + "…" : text;
  };

  const doSearch = useCallback(async (q, pg = 0) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setTotalResults(0);
      return;
    }
    setLoading(true);
    try {
      const resp = await api.get(`/blogs/search?q=${encodeURIComponent(q)}&page=${pg}&size=10`);
      const { content, totalElements, totalPages: tp, number } = resp;
      setPage(number);
      setTotalPages(tp);
      setTotalResults(totalElements);
      const processed = (content || []).map(a => ({
        ...a,
        coverImage: a.coverImageUrl || null,
        date: new Date(a.createdAt).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }),
      }));
      if (pg === 0) {
        setResults(processed);
      } else {
        setResults(prev => [...prev, ...processed]);
      }
    } catch (err) {
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search on query change
  useEffect(() => {
    const t = setTimeout(() => {
      if (query) {
        setSearchParams({ q: query });
        doSearch(query, 0);
      } else {
        setResults([]);
        setTotalResults(0);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [query, doSearch, setSearchParams]);

  // Run search on page load if URL has ?q=
  useEffect(() => {
    const q = searchParams.get("q");
    if (q) {
      setQuery(q);
      doSearch(q, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Helmet>
        <title>Search{query ? `: ${query}` : ""} | Digital Journaling</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "24px 16px" }}>
        <h1 style={{ fontSize: "1.6rem", marginBottom: 16, color: "var(--text-color)" }}>Search Articles</h1>

        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search by title, category or description..."
          style={{
            width: "100%", padding: "12px 16px", fontSize: 16,
            borderRadius: 10, border: "1px solid var(--border-color, #ddd)",
            background: "var(--input-bg, #fff)", color: "var(--text-color, #222)",
            outline: "none", boxSizing: "border-box", marginBottom: 20,
          }}
          autoFocus
        />

        {loading && <p style={{ color: "var(--muted-color)" }}>Searching…</p>}

        {!loading && query.length >= 2 && (
          <p style={{ color: "var(--muted-color)", fontSize: 14, marginBottom: 16 }}>
            {totalResults} result{totalResults !== 1 ? "s" : ""} for "<strong>{query}</strong>"
          </p>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {results.map(article => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              style={{ textDecoration: "none", color: "inherit" }}
            >
              <div style={{
                display: "flex", gap: 16, padding: 16,
                background: "var(--card-bg, #fff)", borderRadius: 10,
                boxShadow: "0 2px 8px var(--shadow-color, rgba(0,0,0,0.06))",
                transition: "box-shadow 0.2s",
                border: "1px solid var(--border-color, #eee)",
              }}>
                {article.coverImage && (
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    style={{ width: 100, height: 70, objectFit: "cover", borderRadius: 8, flexShrink: 0 }}
                  />
                )}
                <div>
                  <span style={{
                    fontSize: 11, fontWeight: 600, textTransform: "uppercase",
                    color: "var(--primary-color)", background: "rgba(34,74,190,0.08)",
                    padding: "2px 8px", borderRadius: 20, display: "inline-block", marginBottom: 6,
                  }}>{article.category}</span>
                  <h3 style={{ margin: "0 0 6px", fontSize: "1rem", color: "var(--text-color)" }}>{article.title}</h3>
                  <p style={{ margin: 0, fontSize: 13, color: "var(--muted-color)" }}>{truncate(article.description, 160)}</p>
                  <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 12, color: "var(--muted-color)" }}>
                    <span><FaPenAlt /> {article.author === "sushensable" ? "Sushen Sable" : article.author}</span>
                    <span><FaRegCalendarAlt /> {article.date}</span>
                    <span><FaEye /> {article.views}</span>
                    <span><FaThumbsUp /> {article.likes}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {!loading && page + 1 < totalPages && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button
              onClick={() => doSearch(query, page + 1)}
              style={{
                padding: "10px 28px", borderRadius: 24,
                background: "var(--primary-color, #224abe)", color: "#fff",
                border: "none", cursor: "pointer", fontWeight: 600, fontSize: 14,
              }}
            >
              Load More
            </button>
          </div>
        )}

        {!loading && results.length === 0 && query.length >= 2 && (
          <div style={{ textAlign: "center", padding: "40px 0", color: "var(--muted-color)" }}>
            <p>No articles found for "<strong>{query}</strong>".</p>
            <p style={{ fontSize: 14 }}>Try different keywords or browse by category on the <Link to="/">home page</Link>.</p>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default SearchPage;
